import type { ToolUIPart } from 'ai';
import { type Message as MessageClass } from './message';
import type { ToolPart } from './message-parts';
import type { Receipt } from '../llm/llm-input-types';
import { ingotModelOutput } from './ingot-receipt';

/**
 * Tool-result `output` must be JSON-serializable for {@link modelMessageSchema}.
 * Postgres `timestamptz` columns often hydrate as `Date` at runtime even when
 * typed as `string` — normalize before splicing into the prompt.
 */
function normalizeReceiptForLlmOutput(receipt: Receipt): Receipt {
  const calledAt = receipt['calledAt'];
  if (calledAt === undefined || !(calledAt instanceof Date)) {
    return receipt;
  }
  return { ...receipt, calledAt: calledAt.toISOString() };
}

function applyReceiptsToMessage(
  message: MessageClass,
  receipts: ReadonlyMap<string, Receipt>,
  collapseIngot: boolean
): MessageClass {
  const toolParts = message.getToolParts();
  if (toolParts.length === 0) {
    return message;
  }
  // Clone lazily: only when something actually changes, so untouched messages
  // (and the conversation's shared instances) are never mutated.
  let next: MessageClass | undefined;
  for (const toolPart of toolParts) {
    const output = modelOutputFor(toolPart, receipts, collapseIngot);
    if (output === undefined) {
      continue;
    }
    next ??= message.clone();
    next.replaceToolCallPart(toolPart.toolCallId, {
      ...toolPart.toolPart,
      output,
    } as ToolUIPart);
  }
  return next ?? message;
}

/**
 * The output the model should see for a tool part, or undefined to leave the
 * stored output untouched. An Ingot receipt on the part wins; tool calls from
 * before Ingot fall back to a stored summary receipt when one exists.
 *
 * `collapseIngot` drops an inline Ingot output down to its receipt — set for
 * results in already-completed turns (see {@link applyReceiptsToToolParts}).
 */
function modelOutputFor(
  toolPart: ToolPart,
  receipts: ReadonlyMap<string, Receipt>,
  collapseIngot: boolean
): unknown {
  const ingot = toolPart.ingotReceipt();
  if (ingot !== null) {
    return ingotModelOutput(ingot, toolPart.getOutput(), {
      collapse: collapseIngot,
    });
  }
  const receipt = receipts.get(toolPart.toolCallId);
  return receipt === undefined
    ? undefined
    : normalizeReceiptForLlmOutput(receipt);
}

/**
 * The index of the last completed agent response — an assistant message with
 * real text, which in Coco only ever ends a turn. Tool calls before it belong
 * to turns the model has already answered; everything after is the turn still
 * in flight. -1 when the model has not answered yet.
 */
function lastCompletedResponseIndex(messages: MessageClass[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]!;
    if (
      message.isAssistantMessage() &&
      message.parts.some((part) => part.isTextPart() && part.text.trim() !== '')
    ) {
      return i;
    }
  }
  return -1;
}

/**
 * Walk the prepared {@link MessageClass}es and replace each tool part's
 * `output` with what the model should see: its Ingot receipt (with the output
 * beside it when inline), else its stored {@link Receipt} when one exists.
 * Tool calls with neither are left untouched. Returns {@link MessageClass} instances,
 * leaning on the message's built-in tool-part manipulation rather than
 * hand-editing raw `UIMessage` objects. Messages with no substitutions are
 * returned as-is; substituted ones are cloned so callers' inputs stay intact.
 *
 * With `collapseCompletedTurns`, an inline Ingot result in a turn the model has
 * already answered is dropped to its receipt so the conversation stops carrying
 * every past tool output in full on every turn. The turn still in flight keeps
 * its outputs verbatim — the model needs them to answer. Off by default, so the
 * insights/eval path keeps the size-based (inline-flag) view.
 */
export function applyReceiptsToToolParts(
  messages: MessageClass[],
  receipts: ReadonlyMap<string, Receipt>,
  options?: { collapseCompletedTurns?: boolean }
): MessageClass[] {
  const collapseBefore =
    options?.collapseCompletedTurns === true
      ? lastCompletedResponseIndex(messages)
      : -1;
  return messages.map((message, index) =>
    applyReceiptsToMessage(message, receipts, index < collapseBefore)
  );
}
